import React, { useEffect, useMemo, useRef, useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Section, Seal, StatCard, Chip, Spinner } from "../components/ui.jsx";
import Photo from "../components/Photo.jsx";
import { primaryBtnStyle, secondaryBtnStyle, ghostBtnStyle, inputStyle } from "../components/styles.js";
import { sortedHistory } from "../domain/history.js";
import { fmtMoney, fmtDate, toISODate } from "../domain/format.js";
import { generatePreservationProfile, materialTipsFor, maintenanceStatus } from "../domain/preservation.js";
import { STATUS_OPTIONS, statusMeta } from "../domain/status.js";
import { categoryMeta } from "../domain/categories.js";
import { downloadICS } from "../lib/ics.js";
import PreservationScanner from "../components/PreservationScanner.jsx";

/* One logged Flight / Clean / Rotation.

   The date is held locally while it is being edited and only lifted up on the
   way out of the field. Reporting every keystroke instead looks reasonable but
   isn't: a date input emits a change per digit — typing the year 2027 emits
   0002, 0020, 0202 first — and re-rendering the whole list on each one both
   resets which segment of the field has focus (so the next digit lands in the
   month) and re-sorts the list, moving the row out from under the cursor. */
function HistoryRow({ entry, onChangeDate, onDelete }) {
  const [draft, setDraft] = useState(entry.date);
  const editing = useRef(false);

  // Follow the entry if it changes underneath us — a reload, or the server
  // correcting our optimistic guess — but never mid-edit.
  useEffect(() => {
    if (!editing.current) setDraft(entry.date);
  }, [entry.date]);

  function commit() {
    editing.current = false;
    // An empty or unchanged field is not an edit; put the real date back.
    if (draft && draft !== entry.date) onChangeDate(draft);
    else setDraft(entry.date);
  }

  /* Leaving the screen with the field still focused — a back gesture, a closed
     tab — would otherwise drop the edit on the floor without saying so. The
     `deleted` flag keeps that from firing when the row is on its way out:
     re-dating an entry that has just been removed only earns an error about a
     row that no longer exists. */
  const deleted = useRef(false);
  const pending = useRef(null);
  pending.current = { draft, date: entry.date, onChangeDate };
  useEffect(() => {
    return () => {
      if (deleted.current) return;
      const { draft: d, date, onChangeDate: save } = pending.current;
      if (d && d !== date) save(d);
    };
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: COLORS.blackCard, border: `1px solid ${COLORS.lineFaint}`, borderRadius: 10, padding: "8px 10px" }}>
      <span style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.cream, flex: 1 }}>{entry.action}</span>
      <input
        type="date"
        value={draft}
        onFocus={() => {
          editing.current = true;
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        style={{ ...inputStyle, padding: "5px 8px", fontSize: 11.5, width: 130, flex: "none" }}
      />
      <button
        onClick={() => {
          deleted.current = true;
          onDelete();
        }}
        title="Remove this entry"
        aria-label="Remove this entry"
        style={{ width: 24, height: 24, borderRadius: "50%", background: "transparent", border: `1px solid ${COLORS.red}`, color: COLORS.red, fontSize: 12, cursor: "pointer", lineHeight: "22px", flex: "none" }}
      >
        ×
      </button>
    </div>
  );
}

export default function ItemDetail({
  item, photoUrls, onBack, onEdit, onLogWear, onUpdateCondition, onLogAction, onUpdateStatus,
  onAddPhotos, onDeletePhoto, onRefreshPhoto, onDeleteHistoryEntry, onUpdateHistoryEntry, onApplyScan,
}) {
  const ms = maintenanceStatus(item);
  const meta = categoryMeta(item.category);
  const profile = useMemo(() => generatePreservationProfile(item), [item]);
  const matTips = useMemo(() => materialTipsFor(item), [item]);
  const history = useMemo(() => sortedHistory(item.history), [item.history]);
  const [uploading, setUploading] = useState(false);

  async function handlePhotoFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    await onAddPhotos(item.id, files);
    setUploading(false);
  }

  const maintCardStyle = { background: COLORS.blackCard, border: `1px solid ${COLORS.lineFaint}`, borderRadius: 12, padding: 14 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onBack} style={ghostBtnStyle}>
          ← Back to Passport
        </button>
        <button
          onClick={() => onEdit(item.id)}
          style={{
            background: "rgba(201,162,39,0.12)",
            color: COLORS.goldBright,
            border: `1px solid ${COLORS.gold}`,
            borderRadius: 8,
            padding: "7px 16px",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: FONT_BODY,
            cursor: "pointer",
            flex: "none",
          }}
        >
          ✎ Edit
        </button>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <Seal value={item.condition} size={72} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 21, color: COLORS.cream }}>
            {item.brand} {item.model}
          </div>
          <div style={{ fontSize: 13.5, color: COLORS.creamDim, fontFamily: FONT_BODY }}>
            {item.colorway ? `${item.colorway} · ` : ""}
            {item.size ? `Size ${item.size}` : meta.label}
            {item.sku ? ` · ${item.sku}` : ""}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <StatCard label="Est. market value" value={fmtMoney(item.value)} accent={COLORS.goldBright} />
        <StatCard label="Retail price" value={fmtMoney(item.retail)} />
        <StatCard label="Acquired" value={fmtDate(item.dateAdded)} />
        <StatCard
          label={`Total ${meta.wearVerbPlural.toLowerCase()}`}
          value={item.wears}
          sub={item.lastWorn ? `Last: ${fmtDate(item.lastWorn)}` : "Never used"}
        />
      </div>

      <Section title="Photos">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {(item.photos || []).map((path) => (
            <div key={path} style={{ position: "relative", width: 72, height: 72 }}>
              <Photo
                src={photoUrls[path]}
                onExpired={() => onRefreshPhoto(path)}
                style={{ width: 72, height: 72, borderRadius: 10, objectFit: "cover", border: `1px solid ${COLORS.lineFaint}`, display: "block" }}
                fallback={
                  <div style={{ width: 72, height: 72, borderRadius: 10, border: `1px solid ${COLORS.lineFaint}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spinner size={14} />
                  </div>
                }
              />

              <button
                onClick={() => onDeletePhoto(item.id, path)}
                aria-label="Remove photo"
                style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: COLORS.red, color: "#fff", border: "none", fontSize: 11, cursor: "pointer", lineHeight: "20px" }}
              >
                ×
              </button>
            </div>
          ))}
          <label
            style={{
              width: 72, height: 72, borderRadius: 10, border: `1px dashed ${COLORS.line}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: uploading ? "wait" : "pointer", color: COLORS.creamFaint, fontSize: 22, fontFamily: FONT_BODY,
            }}
          >
            {uploading ? <Spinner size={16} /> : "+"}
            <input type="file" accept="image/*" multiple disabled={uploading} onChange={handlePhotoFiles} style={{ display: "none" }} />
          </label>
        </div>
      </Section>

      <Section title="AI Preservation Scanner">
        <PreservationScanner item={item} onApply={onApplyScan} />
      </Section>

      <Section title="Flight History">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {history.length === 0 && (
            <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>No activity logged yet.</div>
          )}
          {history.map((h) => (
            <HistoryRow
              key={h.id}
              entry={h}
              onChangeDate={(date) => onUpdateHistoryEntry(item.id, h.id, date)}
              onDelete={() => onDeleteHistoryEntry(item.id, h.id)}
            />
          ))}
        </div>
        <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 8 }}>
          Fix an accidental or duplicate log here — deleting a Flight, Clean, or Rotation updates the item's totals automatically.
        </div>
      </Section>

      <Section title="Wear-habit status">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {STATUS_OPTIONS.map((s) => (
            <Chip key={s.key} label={s.label} active={item.status === s.key} onClick={() => onUpdateStatus(item.id, s.key)} />
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 8 }}>{statusMeta(item.status).note}</div>
      </Section>

      <Section title="Flight Tracker">
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => onLogWear(item.id)} style={primaryBtnStyle}>
            Log {meta.wearVerb === "Outing" ? "an" : "a"} {meta.wearVerb}
          </button>
        </div>
      </Section>

      <Section title="Smart Maintenance">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={maintCardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.cream }}>Cleaning</span>
              {ms.cleanDue && <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: COLORS.red }}>DUE</span>}
            </div>
            <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 4 }}>
              Last: {fmtDate(item.lastCleaned)}
              {ms.nextCleanDate && ` · Next due: ${fmtDate(toISODate(ms.nextCleanDate))}`}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={() => onLogAction(item.id, "Cleaned")} style={{ ...secondaryBtnStyle, padding: "9px 14px", fontSize: 12.5 }}>
                Log a clean
              </button>
              {ms.nextCleanDate && (
                <button
                  onClick={() => downloadICS(`Clean: ${item.brand} ${item.model}`, ms.nextCleanDate, "SENOGARA maintenance reminder")}
                  style={{ ...ghostBtnStyle, fontSize: 12.5 }}
                >
                  + Calendar
                </button>
              )}
            </div>
          </div>

          {statusMeta(item.status).rotateDays != null && (
            <div style={maintCardStyle}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.cream }}>Rotation</span>
                {ms.rotateDue && <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: COLORS.red }}>DUE</span>}
              </div>
              <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 4 }}>
                Last: {fmtDate(item.lastRotated)}
                {ms.nextRotateDate && ` · Next due: ${fmtDate(toISODate(ms.nextRotateDate))}`}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={() => onLogAction(item.id, "Rotated")} style={{ ...secondaryBtnStyle, padding: "9px 14px", fontSize: 12.5 }}>
                  Log a rotation
                </button>
                {ms.nextRotateDate && (
                  <button
                    onClick={() => downloadICS(`Rotate: ${item.brand} ${item.model}`, ms.nextRotateDate, "SENOGARA maintenance reminder")}
                    style={{ ...ghostBtnStyle, fontSize: 12.5 }}
                  >
                    + Calendar
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section title={`${meta.label} preservation profile`}>
        <div style={{ background: COLORS.blackCard, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: 16 }}>
          {profile.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "8px 0", borderTop: `1px solid ${COLORS.lineFaint}`, fontSize: 12.5, fontFamily: FONT_BODY }}>
              <span style={{ color: COLORS.creamDim, flex: "none" }}>{k}</span>
              <span style={{ color: COLORS.cream, textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
      </Section>

      {matTips.length > 0 && (
        <Section title="Material-specific care">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {matTips.map(([m, tip], i) => (
              <div key={`${m}-${i}`} style={{ fontSize: 12.5, color: COLORS.creamDim, fontFamily: FONT_BODY }}>
                <b style={{ color: COLORS.goldBright }}>{m}:</b> {tip}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Condition score">
        <input
          type="range"
          min="0"
          max="100"
          value={item.condition}
          onChange={(e) => onUpdateCondition(item.id, Number(e.target.value))}
          style={{ width: "100%", accentColor: COLORS.gold }}
        />
      </Section>
    </div>
  );
}
